import {put, call, all} from 'redux-saga/effects';
import Option from "../../entities/option";
import * as Actions from "../actions/optionActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type OptionAction = {
    optionId: string;
    option: Option;
    options: Option[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneOption(action: OptionAction) {
	const session = tabSession.captureGeneration();
	try {
		const option: Option = yield call(api.findOne, 'option', action.optionId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.OPTION_FETCH_ONE_SUCCESS, optionId: action.optionId, option, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.OPTION_FETCH_ONE_FAILURE, optionId: action.optionId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {OptionAction} action
 */
export function* saveOption(action: OptionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ OPTION_SAVE saga', action.option?.id);

		if (action.option === undefined || action.option === null) {
			console.error('saveOption received a null');
			throw new Error('saveOption received a null');
		}

		if (!(action.option instanceof Option)) {
			action.option = new Option(action.option);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.option.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.option);
			const addResult = yield call(idb.add, 'option', action.option);
			if (addResult.isErr()) {
				console.error(`⮂ saveOption: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertOptionSave(action.option, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.OPTION_SAVE_FAILURE, option: action.option, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.OPTION_SAVE, option: action.option, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertOptionSave(action.option, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.OPTION_SAVE_FAILURE, option: action.option, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {OptionAction} action
 * @param {Option[]} action.options
 */
export function* saveManyOptions(action: OptionAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ OPTION_SAVE_MANY saga', action.options.map(option => option.id).join(','));

        if (!Array.isArray(action.options)) {
            console.error('saveManyOptions expected an array, received', action.options);
            throw new Error('saveManyOptions expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveOption), under these queue keys
		const dispatchedAddedOns: number[] = action.options.map(option => option?.addedOn);
		const revertAll = () => action.options.map((option, i) =>
			put(Actions.revertOptionSave(option, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Option[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.options.length; i++) {
				const option = action.options[i];
				option.persisted = false;
				ensureOperationId(option);
				toSave.push(option);
			}

			const addResult = yield call(idb.add, 'option', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyOptions: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.OPTION_SAVE_MANY_FAILURE, options: action.options, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.OPTION_SAVE_MANY, options: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.OPTION_SAVE_MANY_FAILURE, options: action.options, status: 500, err});

			console.error(`Error saving ${action.options.length} options to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {OptionAction} action
 */
export function* removeOption(action: OptionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.option === undefined || action.option === null || !(action.option instanceof Option)) {
			console.error('removeOption expected type Option, received', action.option);
			reportQueued(false);
			throw new Error('removeOption expected type Option');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.option.status = 'D';
		action.option.addedOn = newHRTimestamp();
		action.option.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.option);
		const addResult = yield call(idb.add, 'option', action.option);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeOption: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.OPTION_REMOVE_FAILURE, option: action.option, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.OPTION_REMOVE, option: action.option});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.OPTION_REMOVE_FAILURE, option: action.option, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Option.
	*
	* @param {OptionAction} action
	*/
export function* restoreOption(action: OptionAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreOption expected getCurrentUserId() to return a value');
        return;
    }

	if (action.option === undefined || action.option === null) {
        console.error('restoreOption expected a Option, received null');
        throw new Error('restoreOption expected a Option, received null');
    }
    else if (!(action.option instanceof Option)) action.option = new Option(action.option); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.option.status = 'U';
		action.option.addedOn = newHRTimestamp();
		action.option.addedBy = userId;
		action.option.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.option);
		const addResult = yield call(idb.add, 'option', action.option);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreOption: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.OPTION_RESTORE_FAILURE, option: action.option, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.OPTION_RESTORE, option: action.option});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.OPTION_RESTORE_FAILURE, option: action.option, status: 500, err});

		console.error('Error restoring from database', err);
	}
}